'use client';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

interface ReCaptchaProps {
	siteKey: string;
	action: string;
	onVerify: (token: string) => void;
}

export interface ReCaptchaHandle {
	refresh: () => void;
}

// Define window with recaptcha properties
declare global {
	interface Window {
		grecaptcha: {
			ready: (callback: () => void) => void;
			execute: (siteKey: string, options: { action: string }) => Promise<string>;
		};
		onRecaptchaLoad?: () => void;
	}
}

const RECAPTCHA_SCRIPT_ID = 'recaptcha-script';
const RECAPTCHA_API_URL = 'https://www.google.com/recaptcha/api.js';

const ReCaptcha = forwardRef<ReCaptchaHandle, ReCaptchaProps>(
	({ siteKey, action, onVerify }, ref) => {
		const loaded = useRef(false);

		// Function to execute reCAPTCHA and get a new token
		const executeReCaptcha = useCallback(() => {
			if (!loaded.current && !window.grecaptcha) {
				return;
			}

			window.grecaptcha.ready(() => {
				window.grecaptcha
					.execute(siteKey, { action })
					.then(onVerify)
					.catch(error => {
						console.error('reCAPTCHA execution failed:', error);
					});
			});
		}, [siteKey, action, onVerify]);

		// Expose the refresh method to parent components
		useImperativeHandle(ref, () => ({
			refresh: () => {
				executeReCaptcha();
			}
		}));

		useEffect(() => {
			// Skip if already loaded
			if (document.getElementById(RECAPTCHA_SCRIPT_ID) || loaded.current) {
				executeReCaptcha();
				return;
			}

			// Set up callback for when the script loads
			window.onRecaptchaLoad = () => {
				loaded.current = true;
				executeReCaptcha();
			};

			// Load the reCAPTCHA script
			const script = document.createElement('script');
			script.id = RECAPTCHA_SCRIPT_ID;
			script.src = `${RECAPTCHA_API_URL}?render=${siteKey}`;
			script.async = true;
			script.defer = true;
			script.onload = window.onRecaptchaLoad;
			document.head.appendChild(script);

			return () => {
				// Clean up on unmount
				window.onRecaptchaLoad = undefined;
			};
		}, [siteKey, action, onVerify, executeReCaptcha]);

		// This component doesn't render anything visible
		return null;
	}
);

ReCaptcha.displayName = 'ReCaptcha';

export default ReCaptcha;
